import { useCallback, useEffect, useRef, useState } from 'react'

interface UseMessageScrollNativeOptions {
  currentUserId: string
  onNewMessage?: (isOwn: boolean) => void
}

const AT_BOTTOM_THRESHOLD_PX = 80

/**
 * Hook de scroll para MessageList nativa (sem virtualização).
 *
 * Substitui useMessageScroll (que dependia de VirtuosoHandle).
 *
 * Comportamento (estilo grupos do WhatsApp):
 * - Detecta isAtBottom via onScroll com threshold de 80px
 * - scrollToBottom usa scrollTo nativo (auto = instantâneo, smooth = animado)
 * - Mensagem própria sempre força fim
 * - Mensagem alheia + atBottom força fim; senão incrementa badge
 *
 * Mantém também stickToBottomRef sincronizado com isAtBottom — usado
 * pelo ResizeObserver no componente pra re-colar no fim quando alturas
 * dinâmicas (imagens, markdown) crescem após o paint.
 */
export function useMessageScrollNative({
  currentUserId,
  onNewMessage,
}: UseMessageScrollNativeOptions) {
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const [newMessagesCount, setNewMessagesCount] = useState(0)
  // Mirror em ref pra leitura síncrona em ResizeObserver/handlers.
  const stickToBottomRef = useRef(true)

  useEffect(() => {
    stickToBottomRef.current = isAtBottom
  }, [isAtBottom])

  /** Rola pro fim do scroller. Padrão: animação suave (igual botão). */
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = scrollerRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior })
    setNewMessagesCount(0)
  }, [])

  /** Handler de scroll do container. Coalesce via rAF. */
  const rafRef = useRef<number | null>(null)
  const onScroll = useCallback(() => {
    if (rafRef.current != null) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      const el = scrollerRef.current
      if (!el) return
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight
      const atBottom = dist <= AT_BOTTOM_THRESHOLD_PX
      setIsAtBottom(prev => (prev === atBottom ? prev : atBottom))
      if (atBottom) setNewMessagesCount(0)
    })
  }, [])

  /**
   * Decide o que fazer ao receber nova mensagem.
   * - Própria: snap suave (envio sempre força fim)
   * - Outra + atBottom: snap suave (redundante com ResizeObserver, mas garante)
   * - Outra + rolado pra cima: incrementa badge
   */
  const handleNewMessage = useCallback((senderId: string) => {
    const isOwn = senderId === currentUserId
    onNewMessage?.(isOwn)

    if (isOwn) {
      requestAnimationFrame(() => scrollToBottom('smooth'))
    } else if (stickToBottomRef.current) {
      requestAnimationFrame(() => scrollToBottom('smooth'))
    } else {
      setNewMessagesCount(prev => prev + 1)
    }
  }, [currentUserId, onNewMessage, scrollToBottom])

  /** Reseta estado (usado ao trocar de canal). */
  const resetState = useCallback(() => {
    setIsAtBottom(true)
    setNewMessagesCount(0)
    stickToBottomRef.current = true
  }, [])

  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  return {
    scrollerRef,
    isAtBottom,
    newMessagesCount,
    scrollToBottom,
    onScroll,
    handleNewMessage,
    resetState,
    stickToBottomRef,
  }
}
